import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Gift, LogOut, Users, Home, Menu, X, ChevronDown, User } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    setIsOpen(false);
    setIsDropdownOpen(false);
    logout();
    navigate('/login');
  };

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="bg-white shadow-md p-4 mb-8 border-b-4 border-xmas-red">
      <div className="container mx-auto flex justify-between items-center">
        <Link to="/" className="text-2xl font-bold text-xmas-red flex items-center gap-2">
          <Gift className="text-xmas-green" /> Family Wishlist
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6">
          {user ? (
            <>
              <Link to="/dashboard" className="nav-link"><Home size={20}/> Dashboard</Link>
              <Link to="/my-wishlist" className="nav-link"><Gift size={20}/> My Wishlist</Link>
              {user?.familyGroups && user.familyGroups.length > 0 ? (
                <Link to="/family-wishlists" className="nav-link"><Users size={20}/> Family Wishlists</Link>
              ) : (
                <span
                  className="flex items-center gap-1 text-gray-400 cursor-not-allowed opacity-60 grayscale"
                  title="Join a group to access the family wishlists"
                >
                  <Users size={20}/> Family Wishlists
                </span>
              )}

              {/* User Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-1 text-gray-700 hover:text-xmas-red font-medium focus:outline-none"
                >
                  {user.name} <ChevronDown size={16} />
                </button>
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-md shadow-lg py-1 z-50">
                    <Link
                      to="/account"
                      onClick={() => setIsDropdownOpen(false)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-xmas-red flex items-center gap-2"
                    >
                      <User size={16} /> Account
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-xmas-red flex items-center gap-2 border-t border-gray-100"
                    >
                      <LogOut size={16} /> Log Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link to="/login" className="text-gray-600 hover:text-xmas-red font-medium">
              Sign In
            </Link>
          )}
        </div>

        {/* Mobile Navigation controls */}
        <div className="md:hidden flex items-center">
          {user ? (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 hover:text-xmas-red focus:outline-none"
              aria-label="Toggle Navigation Menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          ) : (
            <Link to="/login" className="text-gray-600 hover:text-xmas-red font-medium">
              Sign In
            </Link>
          )}
        </div>
      </div>

      {/* Mobile Navigation Dropdown */}
      {user && isOpen && (
        <div className="md:hidden flex flex-col gap-4 mt-4 pt-4 border-t border-gray-100">
          <Link to="/dashboard" onClick={() => setIsOpen(false)} className="nav-link">
            <Home size={20}/> Dashboard
          </Link>
          <Link to="/my-wishlist" onClick={() => setIsOpen(false)} className="nav-link">
            <Gift size={20}/> My Wishlist
          </Link>
          {user?.familyGroups && user.familyGroups.length > 0 ? (
            <Link to="/family-wishlists" onClick={() => setIsOpen(false)} className="nav-link">
              <Users size={20}/> Family Wishlists
            </Link>
          ) : (
            <span
              className="flex items-center gap-1 text-gray-400 cursor-not-allowed opacity-60 grayscale"
              title="Join a group to access the family wishlists"
            >
              <Users size={20}/> Family Wishlists
            </span>
          )}
          <div className="border-t border-gray-100 pt-2">
            <div className="px-4 py-1 text-sm text-gray-500 font-medium">
              Logged in as {user.name}
            </div>
            <Link
              to="/account"
              onClick={() => setIsOpen(false)}
              className="text-gray-600 hover:text-xmas-red flex items-center gap-1 text-left w-full px-4 py-2"
            >
              <User size={20}/> Account
            </Link>
            <button
              onClick={handleLogout}
              className="text-gray-600 hover:text-xmas-red flex items-center gap-1 text-left w-full px-4 py-2 border-t border-gray-100"
            >
              <LogOut size={20}/> Log Out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
